import { Link } from 'react-router-dom';
import { Button, Typography, Grid, Card, CardContent, Box, LinearProgress, Alert, Chip, Tooltip } from '@mui/material';
import { useState, useEffect } from 'react';
import { useAuth } from "../../auth/AuthContext";
import { getTools, Tool } from "../../utils/billingUtil";
import { ToolsUsageMap, toolUsageApi } from "../../utils/toolUsageUtil";
import theme from "../../theme";

const LoggedInHomepage = () => {
  const [tools, setTools] = useState<Tool[] | null>(null);
  const [allTools, setAllTools] = useState<Tool[] | null>(null);
  const [usageMap, setUsageMap] = useState<ToolsUsageMap>({});
  const [err, setErr] = useState<string | null>(null);
  const { getAccessToken } = useAuth();

  useEffect(() => {
    (async () => {
      try {
        const token = await getAccessToken?.();
        const { tools: fetchedTools } = await getTools(token ?? undefined);
        const map = await toolUsageApi.getAllToolsUsage(token!);
        setAllTools(fetchedTools);
        setTools(
          fetchedTools.filter((tool: Tool) => tool.status.toLowerCase() === "active")
        );
        setUsageMap(map);
      } catch (e: any) {
        setErr(e?.message ?? String(e));
      }
    })();
  }, [getAccessToken]);

  // Loading or error state
  if (err) {
    return (
      <Alert severity="error" variant="outlined" sx={{ my: 1 }}>
        {err}
      </Alert>
    );
  }

  if (!tools || !allTools) {
    return <LinearProgress sx={{ my: 1 }} />;
  }

  return (
    <Card sx={{ p: 3, m: 3 }}>
      <Typography
        variant="h4"
        color={theme.palette.primary.contrastText}
        fontWeight="bold"
        mb={4}
        textAlign="center"
      >
        Your Toolbox Dashboard
      </Typography>

      {tools.length === 0 ? (
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h5" color="primary" mb={2}>
            Build Your Toolbox
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}>
            Discover powerful tools to optimize your New Relic environment. Start with the Optimization Ingest Scheduler to recover up to 50% on ingest costs, or explore more in the Marketplace.
          </Typography>
          <Button variant="contained" color="primary" component={Link} to="/managesubscriptions" sx={{ mb: 6 }}>
            Explore Marketplace
          </Button>
        </Box>
      ) : (
        <Grid container spacing={3} justifyContent="center">
          {tools!.map(tool => (
            <Grid item xs={12} sm={6} md={4} key={tool.code}>
              <Card
                variant={"gradient" as any}
                sx={{ textAlign: 'center', p: 2, height: '100%' }}
              >
                <Box
                  component="img"
                  src={`/tool_icons/${tool.code}.png`}
                  alt={`${tool.name} icon`}
                  onError={(e) => { (e.currentTarget as HTMLImageElement).src = "/tool_icons/NRTB.png"; }}
                  sx={{ width: 100, height: 100, mb: 2 }}
                />
                <Typography variant="h5" mb={1}>
                  {tool.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  {tool.blurb}
                </Typography>
                <Typography variant="subtitle2" color="secondary.light" mb={2}>
                  {usageMap[tool.code]?.usedQty || 0} / {usageMap[tool.code]?.subscribedQty || 0} {tool.unitName} used
                </Typography>
                <Tooltip title="View and manage this tool" arrow>
                  <Button
                    variant="outlined"
                    color="primary"
                    component={Link}
                    to={`/${tool.code.toLowerCase()}`}
                    fullWidth
                  >
                    Go to Tool
                  </Button>
                </Tooltip>
              </Card>
            </Grid>
          ))}
          <Grid item xs={12} sx={{ textAlign: 'center', mt: 4 }}>
            <Button variant="contained" component={Link} to="/managesubscriptions" sx={{backgroundColor: theme.palette.secondary.light}}>
              Add More Tools
            </Button>
          </Grid>
        </Grid>
      )}
    </Card>
  );
};

export default LoggedInHomepage;